// maxHPMirror.js — the single reconciliation seam between the two HP maxima.
//
// [WHY THIS EXISTS — BUG-MAXHP-STORED-VS-COMPUTED-DIVERGE, runtime-reproduced 2026-07-18]:
// playerStats.maxHitPoints (STORED) was maintained incrementally (creation, level-up,
// feat deltas) and could never see CON-item changes, racial per-level HP, registry hp
// effects, or exhaustion halving. effectCalculator.calculateMaxHP() (COMPUTED) sees all
// of them but never wrote back. Long rest healed to COMPUTED while every clamp, the
// CombatStatusStrip, and party sync read STORED — producing a player-visible 24/20.
// After this seam, STORED is a derived mirror of COMPUTED: MainApp calls reconcileMaxHP
// on every input change and writes the result via setPlayerStats. Roll luck survives in
// playerStats.hpRollDelta (Σ rolled − average per level-up), which calculateMaxHP now
// consumes BEFORE exhaustion halving (PHB p.291 — halving applies to the full maximum).
//
// Rule 22: this logic is extracted so App.jsx and the tests import the SAME functions.

/**
 * Decide whether stored maxHitPoints must be reconciled to the computed maximum.
 *
 * @param {Object} playerStats - current player stats (STORED side)
 * @param {number} computedMax - effectCalculator.calculateMaxHP(...) result (COMPUTED side)
 * @returns {null | { maxHitPoints: number, currentHitPoints?: number, note: string }}
 *   null = do not write (hydrating, invalid input, or already in sync).
 */
export function reconcileMaxHP(playerStats, computedMax) {
  if (!playerStats) return null;

  // Hydration window — calculateMaxHP returns its silent fallback (8) while classes /
  // constitution are still hydrating (effectCalculator.js:685-689). Mirroring that
  // fallback over a real 30 would corrupt a healthy character. Same guard shape.
  const hasNoClasses = !Array.isArray(playerStats.classes) || playerStats.classes.length === 0;
  const conIsUnset = playerStats.constitution === undefined || playerStats.constitution === null;
  if (hasNoClasses || conIsUnset) return null;

  if (!Number.isFinite(computedMax) || computedMax < 1) return null;

  const stored = playerStats.maxHitPoints;
  if (stored === computedMax) return null;

  const result = {
    maxHitPoints: computedMax,
    note: `Max HP recalculated: ${stored} → ${computedMax} (unified HP calculation)`,
  };

  // The real maximum dropped below current HP (e.g. exhaustion 4 halving, feat removal,
  // CON item unequipped): current must come down with it. Never raise current here —
  // healing is a game action, not a bookkeeping side effect.
  const current = playerStats.currentHitPoints;
  if (typeof current === 'number' && current > computedMax) {
    result.currentHitPoints = computedMax;
  }

  return result;
}

/**
 * Per-level-up roll-luck delta: what was taken minus what the average option was.
 * Mirrors LevelUpWizard's exact formulas (LevelUpWizard.js:1243-1250): both sides
 * carry the same conModifier and the same Math.max(1, …) floor, so for tookAverage
 * the delta is exactly 0 and for a roll it is the die-luck alone.
 *
 * @param {Object} levelUpData - { rolledHP, conModifier, tookAverage }
 * @param {Object} classData - resolved class ({ hitDice: '1d8' | 'd8' | number })
 * @returns {number} delta to accumulate into playerStats.hpRollDelta
 */
export function levelUpHpRollDelta(levelUpData, classData) {
  if (!levelUpData || typeof levelUpData.rolledHP !== 'number') return 0;
  if (levelUpData.tookAverage) return 0;

  let die = 8;
  const hitDiceValue = classData?.hitDice;
  if (typeof hitDiceValue === 'string') {
    const m = hitDiceValue.match(/d(\d+)/i);
    if (m) die = parseInt(m[1], 10);
  } else if (typeof hitDiceValue === 'number') {
    die = hitDiceValue;
  }

  const conMod = typeof levelUpData.conModifier === 'number' ? levelUpData.conModifier : 0;
  const averageGain = Math.max(1, Math.floor(die / 2) + 1 + conMod);
  return levelUpData.rolledHP - averageGain;
}
